// Kohorten-Aggregation für Sammeltermin-Planung
// Gruppiert VGÜ-Fälligkeiten nach Monat + Tatbestand

import type { VguFaelligkeit } from './vorsorgeService';

export interface Kohorte {
  month:          string;    // "2026-06"
  monthLabel:     string;    // "Juni 2026"
  tatbestandCode: string;    // "L1", "V2", etc.
  faellig:        number;    // OFFEN + UEBERFAELLIG
  ueberfaellig:   number;
  erledigt:       number;
  mitarbeiterKennungen: string[];
  vorschlagTermin: string;  // ISO-Date des vorgeschlagenen Sammeltermins
}

function nextMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  // 0=So, 1=Mo ... 6=Sa
  const daysUntilMonday = day === 1 ? 7 : (8 - day) % 7 || 7;
  d.setDate(d.getDate() + daysUntilMonday);
  return d;
}

function vorschlagFuerMonat(month: string): string {
  const [y, m] = month.split('-').map(Number);
  // Letzter Tag des Monats
  const lastDay = new Date(y!, m!, 0);
  // Nächster Montag ab letztem Tag des Vormonats (ca. Mitte des Fälligkeitsmonats)
  const midMonth = new Date(y!, m! - 1, 15);
  const monday   = nextMonday(midMonth);
  // Wenn Montag nach Monatsende → letzten Montag im Monat nehmen
  if (monday.getMonth() !== m! - 1) {
    const prev = new Date(lastDay);
    prev.setDate(lastDay.getDate() - ((lastDay.getDay() + 6) % 7)); // letzter Montag
    return prev.toISOString().slice(0, 10);
  }
  return monday.toISOString().slice(0, 10);
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y!, m! - 1, 1).toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });
}

export function aggregateFaelligkeitenToKohorten(
  faelligkeiten: VguFaelligkeit[],
): Kohorte[] {
  const map = new Map<string, Kohorte>();

  for (const f of faelligkeiten) {
    const month = f.faelligkeitDatum.slice(0, 7);
    const key   = `${month}::${f.tatbestandCode}`;

    if (!map.has(key)) {
      map.set(key, {
        month,
        monthLabel:      monthLabel(month),
        tatbestandCode:  f.tatbestandCode,
        faellig:         0,
        ueberfaellig:    0,
        erledigt:        0,
        mitarbeiterKennungen: [],
        vorschlagTermin: vorschlagFuerMonat(month),
      });
    }

    const k = map.get(key)!;

    if (f.status === 'ERLEDIGT') {
      k.erledigt += 1;
    } else {
      k.faellig += 1;
      if (f.status === 'UEBERFAELLIG') { k.ueberfaellig += 1; }
    }

    if (!k.mitarbeiterKennungen.includes(f.mitarbeiterKennung)) {
      k.mitarbeiterKennungen.push(f.mitarbeiterKennung);
    }
  }

  return Array.from(map.values())
    .sort((a, b) => a.month.localeCompare(b.month) || a.tatbestandCode.localeCompare(b.tatbestandCode));
}
